/**
 * BusyOverlay A widget that places a semi-opaque div and spinner over a target widget to indicate
 * it's busy. Optionally, a text can be set under the spinner to give some context to it.
 *
 * @module mw-overlay-utils/BusyOverlay
 * @copyright Copyright 2013-19 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/on",
    "dojo/Deferred",
    "dojo/promise/all",
    "dijit/registry",
    "dijit/_WidgetBase",
    "dijit/_TemplatedMixin",
    "mw-progress-indicator/ProgressIndicator",
    "mw-log/Log",
    "dojo/text!./templates/busyOverlayTemplate.html"
], function (declare, on, Deferred, all, registry, _WidgetBase, _TemplatedMixin, ProgressIndicator,
             Log, template) {

    /**
     * options for a Large spinner
     * @type {Object}
     */
    var largeOption = {
        spinnerSize: 'large'
    };

    /**
     * options for a Medium spinner
     * @type {Object}
     */
    var mediumOption = {
        spinnerSize: 'medium'
    };

    /**
     * options for a Small spinner
     * @type {Object}
     */
    var smallOption = {
        spinnerSize: 'small'
    };

    /**
     * options for a X-Spinner spinner
     * @type {Object}
     */
    var xsmallOption = {
        spinnerSize: 'xsmall'
    };


    /**
     * @class BusyOverlay
     * @classdesc A widget that displays a busy state and does not allow any user inputs for the
     * target widget at that time. It does this by placing a semi-opaque div and spinner over the
     * target element.
     *
     * @param {object} options - Options can either be {BusyOverlay.SIZE} size The size of
     * the spinner or it can be an object with two properties viz. SIZE and TEXT. It defaults to
     * (BusyOverlay.SIZE.LARGE) and empty text.
     * @param {object} options.size - {BusyOverlay.SIZE}
     * @param {string} options.text - A string that can be set as the text under the spinner.
     * @example <caption>BusyOverlay object without text</caption>
     * var busyIndicator = new BusyOverlay(BusyOverlay.SIZE.XSMALL);
     * @example <caption>BusyOverlay object with text</caption>
     * var options = {
     *     size: BusyOverlay.SIZE.XSMALL,
     *     text: "Hi, I am the text under spinner.I am a very big text for this div."
     * };
     */
    var BusyOverlay = declare("MW.utils.BusyOverlay", [_WidgetBase, _TemplatedMixin], {
        /** @lends BusyOverlay*/
        /**
         * @property {boolean} _displayed Record the current visible state of the overlay
         * @private
         * @instance
         */
        _displayed: false,

        /**
         * @property {boolean} _started Variable to check if the overlay was already initialized
         * @private
         * @instance
         */
        _started: false,

        /**
         * @property {Object} _spinner Variable storing the mw-progress-indicator's spinner object
         * @private
         * @instance
         */
        _spinner: null,

        /**
         * @property {Object} _size Variable storing the spinner size options
         * @private
         * @instance
         */
        _size: null,

        /**
         * @property {Object} target The DOM element under which the BusyOverlay should be placed at
         * (use the set method to set the target)
         * @instance
         */
        target: null,

        _text: "",

        _textSpan: null,

        _spinnerTextNode: null,

        templateString: template,

        _resizeEvent: null,

        constructor: function (options) {
            this._textSpan = document.createElement("span");
            this._spinnerTextNode = document.createElement("div");

            // Create TEXT property
            Object.defineProperty(this, 'text', {
                set: function (spinnerText) {
                    this._text = spinnerText;
                    !this._resizeEvent && this.registerResize();
                    if (this._text && typeof(this._text) === "string") {
                        this._textSpan.textContent = this._text;
                    } else {
                        this._text = "";
                        this._textSpan.textContent = this._text;
                    }
                    if (this._started) {
                        this._updateTitle();
                    }
                },
                get: function () {
                    return this._text;
                },
                enumerable: true,
                configurable: true
            });

            this._setSpinnerSize(options);
            this._spinnerTextNode.classList.add("spinnerText");
            if (options && options.text) {
                this.set("text", options.text);
            }
        },

        /**
         * Function to update the size of the Spinner.
         * @private
         * @instance
         * @param {object} options - is same as the constructor's parameter. Can be SIZE Enum or object with size and text property.
         */
        _setSpinnerSize: function (options) {
            try {
                // Default large size if options and size is not defined
                if (options === null || options === undefined) {
                    this._size = largeOption;
                } else if (typeof(options) === "object") {
                    if (options.size) {             // Size as a property
                        this._size = options.size;
                    } else if (options.spinnerSize) {
                        this._size = options;      // Options byitself is a SIZE Enum
                    } else {
                        this._size = largeOption;
                    }
                } else {
                    throw new Error('Incorrect argument passed to BusyOverlay Object.');
                }
            } catch (e) {
                Log.error(e.message);
            }
        },

        /**
         * Function to update the title of Spinner Node when the text gets trimmed .
         * If the text width is more than the available width, the text is trimmed with ellipsis at
         * the end and a title is displayed with the full text when hovered over.
         * @private
         * @instance
         */
        _updateTitle: function () {
            this._textSpan.classList.remove("spinnerCenter");         // Work-around for Edge

            // Hiding title as tooltip on top of panel doesn't fit with UX standards
            // if (this._textSpan.offsetWidth >= this._spinnerTextNode.offsetWidth) {
            //     this._spinnerTextNode.setAttribute("title", this._text);
            // } else {
            //     this._spinnerTextNode.setAttribute("title", "");
            // }
            this._textSpan.classList.add("spinnerCenter");
        },

        /**
         * Function to update the style of Spinner text.
         * Based on the text content, the text node is pushed down by some distance.
         * @private
         * @instance
         */
        _updateTextStyle: function () {
            this._spinnerTextNode.style.marginTop = "";
            if (this._text && this.text.toString().trim() !== "") {
                this._spinnerTextNode.style.marginTop = "10px";
            }
        },

        startup: function () {
            if (!this._started) {
                this._spinner = ProgressIndicator({type : 'spinner', size: this._size.spinnerSize});

                if (this.target) {
                    if (typeof this.target === "string") {
                        var targetNode = registry.byId(this.target);
                        this.target = targetNode ? targetNode.domNode : document.getElementById(this.target);
                    }
                    this.target.appendChild(this.domNode);
                    this._spinnerTextNode.appendChild(this._textSpan);
                    // Adding as part of background panel for position alignment
                    this._spinnerBgPanel.appendChild(this._spinnerTextNode);
                }

                if (this._text) {
                    this._updateTitle();
                    !this._resizeEvent && this.registerResize();
                }
                this._started = true;
                this.inherited(arguments);
            }
        },

        postCreate: function () {
            this._underlayNode.style.zIndex = (2e9 - 1);
            this.inherited(arguments);
        },

        /**
         * Function to display the blocking overlay.
         * @instance
         */
        show: function () {
            if (!this._displayed) {

                if (!this._started) {
                    this.startup();
                }

                this._displayed = true;
                this._busyOverlayNode.style.display = "inline";
                this._busyOverlayNode.className = "mw-busyIndicator";
                this._updateTextStyle();
                this._spinnerTextNode.style.display = "block";
                this._spinnerNode.appendChild(this._spinner);
                this.onShow();
            }
        },

        /**
         * Function to hide the blocking overlay.
         * @instance
         */
        hide: function () {
            if (this._displayed) {
                this._displayed = false;
                this._busyOverlayNode.style.display = "none";
                this._spinnerNode.removeChild(this._spinner);
                this._spinnerTextNode.style.display = "none";
                this.onHide();
            }
        },

        /**
         * API to manage Spinner lifecycle when working with promises.
         * Summary: A promise can be passed to the API which will represent a unit of task.
         * 1) If spinner is not visible, we show the spinner and wait for promise to be resolved
         * 2) If spinner is already visible, this promise is added to a vector and dojo/all 
         *    will manage the resolve/reject of this promise
         * Once all the promises inside dojo/all are resolved/rejected, spinner will be removed.
         * @public
         * @instance
         * @param {Promise} promise - promise represneting a unit of task to be completed
         */
        showUntilFinished: function (promise) {
            var deferred = new Deferred();
            promise.always(function() {
                deferred.resolve();
            });

            if (!this.isVisible()) {
                this.show();
            }
            
            if (this._hideBusyIndicatorPromise) {
                this._hideBusyIndicatorPromise = all([this._hideBusyIndicatorPromise, deferred.promise])
            } else {
                this._hideBusyIndicatorPromise = deferred.promise;
            }
            var _currentPromise = this._hideBusyIndicatorPromise;
            _currentPromise.then(function() {
                if (_currentPromise === this._hideBusyIndicatorPromise) {
                    this.hide();
                }
            }.bind(this));
        },


        uninitialize: function () {
            this.hide();
            this.target = null;
            this._spinner = null;
            this._displayed = false;
            if (this._busyOverlayNode) {
                var textNodes = this._busyOverlayNode.querySelectorAll(".spinnerText");
                for (var i = 0; i < textNodes.length; i++) {
                    textNodes[i].remove();
                }
            }
            this._textSpan = null;
            this._spinnerTextNode = null;
            this.inherited(arguments);
            this._resizeEvent && this._resizeEvent.remove();
        },

        /**
         * Helper function so you can test if the widget is already visible or not.
         * @return {boolean} a boolean indicating if the widget is in 'show' state or not.
         * @instance
         */
        isVisible: function () {
            return this._displayed; // boolean
        },

        /**
         * Event that fires when the display of the BusyOverlay completes.
         * @instance
         */
        onShow: function () {
        },

        /**
         * Event that fires when the BusyOverlay successfully hides.
         * @instance
         */
        onHide: function () {
            this.inherited(arguments);
        },
         /**
         * Event that fires when the BusyOverlay DOM container resizes.
         * @instance
         */
        registerResize: function() {
            this._resizeEvent = on(window, "resize", this._updateTextStyle.bind(this));
            this.own(this._resizeEvent);
        }
    });


    /**
     * @enum
     * @readonly
     * @desc A read-only object containing the different available spinner sizes: LARGE, MEDIUM,
     * SMALL, XSMALL
     * @memberof BusyOverlay
     * @static
     */
    var SIZE = {
        /**
         * @desc Display a 48x48px spinner (Used to display for the whole/login page)
         * @type {Object}
         * @static
         */
        LARGE: largeOption,

        /**
         * @desc Display a 32x32px spinner (Used to display within panels)
         * @type {Object}
         * @static
         */
        MEDIUM: mediumOption,

        /**
         * @desc Display a 24x24px spinner (Used to display within a gallery)
         * @type {Object}
         * @static
         */
        SMALL: smallOption,

        /**
         * @desc Display a 16x16px spinner (Used to display within a small area)
         * @type {Object}
         * @static
         */
        XSMALL: xsmallOption
    };

    BusyOverlay.SIZE = SIZE;

    Object.freeze(BusyOverlay.SIZE);
    Object.freeze(BusyOverlay.SIZE.LARGE);
    Object.freeze(BusyOverlay.SIZE.MEDIUM);
    Object.freeze(BusyOverlay.SIZE.SMALL);
    Object.freeze(BusyOverlay.SIZE.XSMALL);

    return BusyOverlay;

});


